<template>
  <div
    class="bekki-assistant-message ga-2 d-flex align-start"
    data-cc="bekki-message"
    :data-state="isStreaming ? 'streaming' : 'assistant'"
  >
    <BekkiAvatar />

    <div
      class="bekki-assistant-message__content d-flex flex-column flex-1-1 ga-3"
    >
      <BekkiThinkingSteps
        v-if="isStreaming && !message.text"
        class="bekki-assistant-message__thinking bekki-assistant-message__enter"
        :steps="steps"
      />

      <div
        v-else-if="message.text"
        v-sanitize="textHtml"
        class="bekki-assistant-message__text text-body-1"
        :class="{ 'bekki-assistant-message__text--streaming': isStreaming }"
        data-cc="bekki-message-text"
        @click="onLinkClick"
      />

      <BekkiChart
        v-for="chart in visibleCharts"
        :key="chart.id"
        class="bekki-assistant-message__enter"
        :chart="chart"
      />

      <BekkiTable
        v-for="table in visibleTables"
        :key="table.id"
        class="bekki-assistant-message__enter"
        :table="table"
      />

      <BekkiNoticeCard
        v-if="message.error"
        class="bekki-assistant-message__enter"
        :code="message.error"
        :isRetryable="isRetryable"
        @retry="emit('retry')"
      />

      <div
        v-if="provenanceText"
        class="bekki-assistant-message__provenance bekki-assistant-message__enter d-flex align-start text-caption-sm font-weight-medium text-disabled"
        data-cc="bekki-provenance"
      >
        <v-icon
          icon="huge:information-circle"
          size="14"
          class="flex-shrink-0 mt-half"
        />
        <span>{{ provenanceText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';

import { useI18n } from 'vue-i18n';

import BekkiAvatar from '@/components/bekki/BekkiAvatar.vue';
import BekkiNoticeCard from '@/components/bekki/BekkiNoticeCard.vue';
import BekkiTable from '@/components/bekki/BekkiTable.vue';
import BekkiThinkingSteps from '@/components/bekki/BekkiThinkingSteps.vue';

import {
  toBekkiLinkLabels,
  useBekkiLinks
} from '@/composables/api/bekki/links';
import type {
  ChatChart,
  ChatMessage,
  ChatProvenance,
  ChatTable,
  DisplayedThinkingStep
} from '@/composables/api/bekki/types';
import dayjs from '@/plugins/dayjs';
import { escapeHtml } from '@/utils/escapeHtml';
import { formatMarkdownToHtml } from '@/utils/markdownToHtml';

interface Props {
  message: ChatMessage;
  isStreaming?: boolean;
  isRetryable?: boolean;
  steps?: DisplayedThinkingStep[];
}

const props = withDefaults(defineProps<Props>(), {
  isStreaming: false,
  isRetryable: false,
  steps: () => []
});

const emit = defineEmits<{
  retry: [];
}>();

const BekkiChart = defineAsyncComponent(
  () => import('@/components/bekki/BekkiChart.vue')
);

const { t } = useI18n();
const { renderLinks, onLinkClick } = useBekkiLinks();

const isAwaitingText = computed(
  (): boolean => props.isStreaming && !props.message.text
);

const visibleCharts = computed((): ChatChart[] =>
  isAwaitingText.value ? [] : (props.message.charts ?? [])
);

const visibleTables = computed((): ChatTable[] =>
  isAwaitingText.value ? [] : (props.message.tables ?? [])
);

const textHtml = computed((): string => {
  const text = props.isStreaming
    ? toBekkiLinkLabels(props.message.text)
    : props.message.text;
  return renderLinks(
    formatMarkdownToHtml(escapeHtml(text), { withStyles: false })
  );
});

const formatPeriod = ({ from, to }: NonNullable<ChatProvenance['period']>) =>
  from === to
    ? dayjs(from).format('ll')
    : `${dayjs(from).format('ll')} – ${dayjs(to).format('ll')}`;

const provenanceText = computed((): string | null => {
  const { provenance } = props.message;
  if (!provenance) return null;

  const time = dayjs(provenance.dataAsOf)
    .tz(provenance.timeZone)
    .format('HH:mm');

  if (provenance.summary) {
    return t('widgets.bekki.provenance', {
      summary: provenance.summary,
      time
    });
  }
  if (provenance.period) {
    return t('widgets.bekki.provenancePeriod', {
      period: formatPeriod(provenance.period),
      time
    });
  }
  return t('widgets.bekki.provenanceAsOf', { time });
});
</script>

<style scoped>
.bekki-assistant-message__content {
  min-width: 0;
  padding-top: 3px;
}

.bekki-assistant-message__text {
  overflow-wrap: anywhere;
}

.bekki-assistant-message__text > :deep(* + *) {
  margin-top: var(--bm-spacing-2);
}

.bekki-assistant-message__text :deep(h3) {
  font-size: inherit;
  font-weight: 500;
}

.bekki-assistant-message__text :deep(ul) {
  padding-inline-start: var(--bm-spacing-5);
}

.bekki-assistant-message__text :deep(strong) {
  font-weight: 600;
}

.bekki-assistant-message__text :deep(.bekki-link) {
  padding-inline: var(--bm-spacing-half) var(--bm-spacing-1);
  background-color: rgba(var(--v-theme-primary), var(--v-opacity-soft));
  box-decoration-break: clone;
  transition: background-color 120ms ease;
}

.bekki-assistant-message__text :deep(.bekki-link:hover) {
  background-color: rgba(var(--v-theme-primary), var(--v-opacity-darker));
}

.bekki-assistant-message__text :deep(.bekki-link:hover .bekki-link__label) {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.bekki-assistant-message__text :deep(.bekki-link:focus-visible) {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: var(--bm-spacing-half);
}

.bekki-assistant-message__text :deep(.bekki-link__icon) {
  width: var(--bm-ic-14);
  height: var(--bm-ic-14);
  margin-inline-end: var(--bm-spacing-half);
  vertical-align: -2px;
}

.bekki-assistant-message__text--streaming > :deep(:last-child:not(ul))::after,
.bekki-assistant-message__text--streaming
  > :deep(ul:last-child > li:last-child)::after {
  content: '';
  display: inline-block;
  width: var(--bm-spacing-half);
  height: 1em;
  margin-inline-start: var(--bm-spacing-half);
  vertical-align: -2px;
  background-color: rgb(var(--v-theme-primary));
  animation: bekki-caret 0.9s steps(1, end) infinite;
}

.bekki-assistant-message__provenance {
  --bekki-enter-delay: 80ms;
  gap: 6px;
  line-height: 18px;
}

.bekki-assistant-message__thinking {
  --bekki-enter-duration: 300ms;
}

.bekki-assistant-message__enter {
  animation: bekki-enter var(--bekki-enter-duration, 260ms) ease-out
    var(--bekki-enter-delay, 0ms) both;
}

@keyframes bekki-enter {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
}

@keyframes bekki-caret {
  0%,
  50% {
    opacity: 1;
  }

  51%,
  100% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .bekki-assistant-message__enter,
  .bekki-assistant-message__text--streaming > :deep(:last-child:not(ul))::after,
  .bekki-assistant-message__text--streaming
    > :deep(ul:last-child > li:last-child)::after {
    animation: none;
  }

  .bekki-assistant-message__text :deep(.bekki-link) {
    transition: none;
  }
}
</style>
